// CreateRoomDialog — creates a channel or agent-only room.
//
// The dialog fields mirror the brief's visual spec: name (required),
// topic (optional), private/public toggle, channel-type pill radio
// (human-only / mixed / agent-only), and an invite autocomplete that
// renders selections as chips. On submit the Matrix room is created
// with the channel-type stamped into `initial_state`, the returned id
// is set active, and the dialog closes.

import { cloneElement, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Sparkles, Users, X } from "lucide-react";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@nurion/ui/select";

import { Dialog } from "~/components/Dialog";
import { usePortalRoot } from "~/components/EmbedPortalContext";
import { GhostButton, PrimaryButton } from "~/components/MessageButtons";
import { UserAvatar, UserSearch } from "~/components/UserSearch";
import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { CHANNEL_TYPES, type ChannelType } from "~/lib/channel-type";
import { getUserProfile, type UserDirectoryEntry } from "~/lib/matrix";
import { spaceDestinationOptions, visibleRoomsForSpace } from "~/lib/spaces";
import { useUnderline } from "~/lib/underline";
import { ICON_STROKE } from "~/lib/icon";

interface CreateRoomDialogProps {
  onClose: () => void;
}

export const ALL_SPACES_OPTION_ID = "__all_spaces__";

export function CreateRoomDialog({ onClose }: CreateRoomDialogProps) {
  const {
    createRoom,
    setActiveRoomId,
    searchUserDirectory,
    client,
    activeOrgSpaceId,
    activeSpaceId,
  } = useMatrix();
  const rooms = useMatrixRooms();
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [isPrivate, setIsPrivate] = useState(true);
  const [channelType, setChannelType] = useState<ChannelType>("mixed");
  const [spaceId, setSpaceId] = useState(activeSpaceId ?? activeOrgSpaceId ?? "");
  const [invitees, setInvitees] = useState<UserDirectoryEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const titleId = `${useId()}-title`;
  const nameId = `${useId()}-name`;
  const topicId = `${useId()}-topic`;
  const spaceIdField = `${useId()}-space`;

  const spaceOptions = useMemo(
    () => [
      { id: ALL_SPACES_OPTION_ID, name: "All spaces" },
      ...spaceDestinationOptions(rooms, activeOrgSpaceId),
    ],
    [rooms, activeOrgSpaceId],
  );

  useEffect(() => {
    if (spaceId && spaceOptions.some((space) => space.id === spaceId)) return;
    const fallback = [activeSpaceId, activeOrgSpaceId].find((id): id is string =>
      Boolean(id && spaceOptions.some((space) => space.id === id)),
    );
    setSpaceId(fallback ?? spaceOptions[0]?.id ?? "");
  }, [activeOrgSpaceId, activeSpaceId, spaceId, spaceOptions]);

  const requestClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [submitting, onClose]);

  // A channel name is duplicated when a visible, non-direct room in the
  // target space already carries the same trimmed, case-insensitive name;
  // the sidebar would otherwise render two indistinguishable rows.
  const isDuplicateName = useMemo(() => {
    const key = name.trim().toLowerCase();
    if (key.length === 0) return false;
    const targetSpaceId = spaceId === ALL_SPACES_OPTION_ID ? null : spaceId;
    return visibleRoomsForSpace(rooms, targetSpaceId).some(
      (room) => !room.isDirect && room.name.trim().toLowerCase() === key,
    );
  }, [name, rooms, spaceId]);

  const canSubmit = name.trim().length > 0 && spaceId.length > 0 && !submitting && !isDuplicateName;

  const visibilityTargetName = useMemo(() => {
    const orgSpaceName =
      spaceOptions.find((option) => option.id === activeOrgSpaceId)?.name ??
      rooms.find((room) => room.id === activeOrgSpaceId)?.name ??
      "Space";
    if (spaceId === ALL_SPACES_OPTION_ID) return orgSpaceName;
    return spaceOptions.find((option) => option.id === spaceId)?.name ?? orgSpaceName;
  }, [spaceId, spaceOptions, rooms, activeOrgSpaceId]);

  async function handleSubmit() {
    // Synchronous ref guard: two rapid clicks read the same pre-repaint
    // render, so the disabled button alone can still pass both — the ref
    // makes the second activation a no-op.
    if (submittingRef.current || !canSubmit) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const roomId = await createRoom({
        name: name.trim(),
        topic: topic.trim() || undefined,
        isPrivate,
        isDirect: false,
        invite: invitees.map((u) => u.userId),
        channelType,
        parentSpaceId: spaceId === ALL_SPACES_OPTION_ID ? null : spaceId,
      });
      setActiveRoomId(roomId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create the channel.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  const handleSelect = useCallback((user: UserDirectoryEntry) => {
    setInvitees((prev) => (prev.some((p) => p.userId === user.userId) ? prev : [...prev, user]));
  }, []);

  const handleRemoveInvitee = useCallback((userId: string) => {
    setInvitees((prev) => prev.filter((p) => p.userId !== userId));
  }, []);

  return (
    <Dialog onClose={requestClose} labelledBy={titleId}>
      <DialogHeader title="Create channel" titleId={titleId} onClose={requestClose} />
      <div
        style={{
          padding: "var(--chat-dialog-body-padding)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-4)",
          overflowY: "auto",
        }}
      >
        <Field id={nameId} label="Name" required>
          <NameInput id={nameId} value={name} onChange={setName} />
        </Field>
        {isDuplicateName && !submitting && (
          <ErrorLine>A channel named “{name.trim()}” already exists in this space.</ErrorLine>
        )}
        <Field id={topicId} label="Topic" hint="What this channel is about">
          <TextInput id={topicId} value={topic} onChange={setTopic} placeholder="Optional" />
        </Field>
        <Field id={spaceIdField} label="Space" required>
          <SpaceSelect
            id={spaceIdField}
            value={spaceId}
            onChange={setSpaceId}
            options={spaceOptions}
          />
        </Field>
        <div>
          <Label>Visibility</Label>
          <Segmented
            value={isPrivate ? "private" : "org"}
            onChange={(v) => setIsPrivate(v === "private")}
            options={[
              { value: "private", label: "Private" },
              { value: "org", label: `Visible to ${visibilityTargetName}` },
            ]}
          />
        </div>
        <div>
          <Label>Channel type</Label>
          <ChannelTypePills value={channelType} onChange={setChannelType} />
        </div>
        <div>
          <Label>Invite</Label>
          {invitees.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "var(--chat-space-1)",
                marginBottom: "var(--chat-space-2)",
              }}
            >
              {invitees.map((u) => (
                <UserChip
                  key={u.userId}
                  name={u.displayName}
                  onRemove={() => handleRemoveInvitee(u.userId)}
                />
              ))}
            </div>
          )}
          <UserSearch
            label="Invite people"
            placeholder="Search by name or @handle"
            onSearch={searchUserDirectory}
            onSelect={handleSelect}
            resolveProfile={client ? (mxid) => getUserProfile(client, mxid) : undefined}
            disabledUserIds={invitees.map((u) => u.userId)}
            disabledLabel="Invited"
          />
        </div>
        {error && <ErrorLine>{error}</ErrorLine>}
      </div>
      <DialogFooter>
        <GhostButton onClick={requestClose} disabled={submitting}>
          Cancel
        </GhostButton>
        <PrimaryButton onClick={() => void handleSubmit()} disabled={!canSubmit}>
          {submitting ? "Creating…" : "Create"}
        </PrimaryButton>
      </DialogFooter>
    </Dialog>
  );
}

/* ─── dialog chrome ─────────────────────────────────────────────── */

export function DialogHeader({
  title,
  titleId,
  onClose,
}: {
  title: string;
  titleId: string;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        height: "var(--chat-dialog-header-height)",
        padding: "0 var(--chat-space-4) 0 var(--chat-space-6)",
        borderBottom: "1px solid var(--chat-surface-border)",
        flexShrink: 0,
      }}
    >
      <h2
        id={titleId}
        style={{
          flex: 1,
          margin: 0,
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-display)",
          fontSize: "var(--chat-type-display-sm-size)",
          lineHeight: "var(--chat-type-display-sm-line)",
          fontWeight: 500,
        }}
      >
        {title}
      </h2>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="chat-button"
        style={{
          width: "var(--chat-dialog-close-size)",
          height: "var(--chat-dialog-close-size)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          background: "transparent",
          color: "var(--chat-fg-secondary)",
          borderRadius: "var(--chat-radius-sm)",
          cursor: "pointer",
        }}
      >
        <X size={14} strokeWidth={ICON_STROKE} />
      </button>
    </div>
  );
}

export function DialogFooter({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        height: "var(--chat-dialog-footer-height)",
        padding: "0 var(--chat-space-6)",
        borderTop: "1px solid var(--chat-surface-border)",
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

/* ─── form primitives ─────────────────────────────────────────── */

function Field({
  id,
  label,
  required,
  hint,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  /**
   * Must be a single React element whose descendant input/textarea
   * carries `id={id}`. `aria-describedby` is cloned onto the element
   * so screen readers announce the hint when the input receives focus
   * — wiring it on a parent wrapper doesn't fire the announcement on
   * focus, only when the wrapper itself receives focus.
   */
  children: React.ReactElement<{ "aria-describedby"?: string }>;
}) {
  const hintId = `${id}-hint`;
  const decoratedChild = hint
    ? cloneElement(children, {
        "aria-describedby": [children.props["aria-describedby"], hintId].filter(Boolean).join(" "),
      })
    : children;
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-1)",
          marginBottom: "var(--chat-space-1)",
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
        }}
      >
        <span>{label}</span>
        {required && (
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 4,
              height: 4,
              borderRadius: "var(--chat-radius-full)",
              background: "var(--chat-fg-muted)",
            }}
          />
        )}
      </label>
      {decoratedChild}
      {hint && (
        <span
          id={hintId}
          style={{
            display: "block",
            marginTop: "var(--chat-space-1)",
            color: "var(--chat-fg-muted)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
          }}
        >
          {hint}
        </span>
      )}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginBottom: "var(--chat-space-1)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      {children}
    </div>
  );
}

function NameInput({
  id,
  value,
  onChange,
  "aria-describedby": ariaDescribedBy,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  "aria-describedby"?: string;
}) {
  const underline = useUnderline();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        height: "var(--chat-directory-row-height)",
        borderBottom: `1px solid ${underline.borderColor}`,
        transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
      }}
    >
      <span
        aria-hidden
        style={{
          marginRight: "var(--chat-space-1)",
          color: "var(--chat-fg-muted)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-md-size)",
        }}
      >
        #
      </span>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={underline.onFocus}
        onBlur={underline.onBlur}
        autoFocus
        maxLength={80}
        aria-describedby={ariaDescribedBy}
        style={{
          flex: 1,
          minWidth: 0,
          border: "none",
          outline: "none",
          background: "transparent",
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-md-size)",
          lineHeight: "var(--chat-type-body-md-line)",
        }}
      />
    </div>
  );
}

export function TextInput({
  id,
  value,
  onChange,
  placeholder,
  "aria-describedby": ariaDescribedBy,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  "aria-describedby"?: string;
}) {
  const underline = useUnderline();
  return (
    <input
      id={id}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={underline.onFocus}
      onBlur={underline.onBlur}
      placeholder={placeholder}
      aria-describedby={ariaDescribedBy}
      style={{
        width: "100%",
        height: "var(--chat-directory-row-height)",
        border: "none",
        borderBottom: `1px solid ${underline.borderColor}`,
        background: "transparent",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-md-size)",
        lineHeight: "var(--chat-type-body-md-line)",
        outline: "none",
        transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
      }}
    />
  );
}

export function SpaceSelect({
  id,
  value,
  onChange,
  options,
  disabled,
  "aria-describedby": ariaDescribedBy,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  options: Array<{ id: string; name: string }>;
  disabled?: boolean;
  "aria-describedby"?: string;
}) {
  const portalRoot = usePortalRoot();
  const isDisabled = disabled || options.length === 0;
  return (
    <Select value={value || undefined} onValueChange={onChange} disabled={isDisabled}>
      <SelectTrigger id={id} aria-describedby={ariaDescribedBy} className="w-full">
        <SelectValue
          placeholder={options.length === 0 ? "No spaces available" : "Choose a space"}
        />
      </SelectTrigger>
      <SelectContent position="popper" portalContainer={portalRoot}>
        {options.map((space) => (
          <SelectItem key={space.id} value={space.id}>
            {space.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/* ─── Segmented + channel-type pills ────────────────────────── */

export function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (next: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div
      role="radiogroup"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--chat-space-4)",
        height: "var(--chat-pill-height)",
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className="chat-button"
            style={{
              padding: 0,
              border: "none",
              background: "transparent",
              color: active ? "var(--chat-fg-primary)" : "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function ChannelTypePills({
  value,
  onChange,
}: {
  value: ChannelType;
  onChange: (next: ChannelType) => void;
}) {
  return (
    <div role="radiogroup" style={{ display: "flex", gap: "var(--chat-space-4)" }}>
      {CHANNEL_TYPES.map((t) => {
        const active = t === value;
        const meta = CHANNEL_TYPE_META[t];
        return (
          <button
            key={t}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(t)}
            className="chat-button"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "var(--chat-space-1)",
              height: "var(--chat-pill-height)",
              padding: 0,
              border: "none",
              background: "transparent",
              color: active ? "var(--chat-fg-primary)" : "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {meta.glyph ? (
              <meta.glyph
                size={14}
                strokeWidth={ICON_STROKE}
                style={{ color: active ? "var(--chat-fg-primary)" : meta.glyphColor }}
              />
            ) : null}
            <span>{meta.label}</span>
          </button>
        );
      })}
    </div>
  );
}

const CHANNEL_TYPE_META: Record<
  ChannelType,
  {
    label: string;
    glyph: typeof Users | null;
    glyphColor: string;
  }
> = {
  "human-only": {
    label: "Human-only",
    glyph: Users,
    glyphColor: "var(--chat-channel-human)",
  },
  mixed: { label: "Mixed", glyph: null, glyphColor: "var(--chat-channel-mixed)" },
  "agent-only": {
    label: "Agent-only",
    glyph: Sparkles,
    glyphColor: "var(--chat-channel-agent)",
  },
};

/* ─── user chip + error line ──────────────────────────────── */

export function UserChip({ name, onRemove }: { name: string; onRemove: () => void }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--chat-space-1)",
        height: "var(--chat-user-chip-height)",
        padding: "0 var(--chat-space-1) 0 2px",
        borderRadius: "var(--chat-radius-full)",
        background: "var(--chat-surface-panel)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      <UserAvatar name={name} size={18} />
      <span style={{ padding: "0 2px" }}>{name}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${name}`}
        className="chat-button"
        style={{
          // Remove affordance sized relative to the chip height so
          // the hit area stays balanced if we grow the chip later.
          width: "calc(var(--chat-user-chip-height) - var(--chat-space-2))",
          height: "calc(var(--chat-user-chip-height) - var(--chat-space-2))",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "var(--chat-radius-full)",
          border: "none",
          background: "transparent",
          color: "var(--chat-fg-secondary)",
          cursor: "pointer",
          padding: 0,
        }}
      >
        <X size={12} strokeWidth={ICON_STROKE} />
      </button>
    </span>
  );
}

export function ErrorLine({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      style={{
        margin: 0,
        color: "var(--chat-state-danger)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      {children}
    </p>
  );
}
